import { useModalStore } from '@store/modalStore';
import { useEntityForModal } from '@api/hooks/useEntityForModal';
import { DocumentReviewModal } from '@components/Review/DocumentReviewModal';
import { APPostingApprovalModal } from '@components/APPosting/APPostingApprovalModal';
import { EditChainModal } from '@components/Admin/ApprovalChains/EditChainModal';
import { CreateHierarchyModal } from '@components/OrgStructure/CreateHierarchyModal';
import { AddMemberModal } from '@components/OrgStructure/AddMemberModal';
import { ReferenceSelectionModal } from '@components/Review/ReferenceSelectionModal';
import { ItemSelectionModal } from '@components/APPosting/ItemSelectionModal';

/**
 * Maps modal names to the entity_type used by useEntityForModal.
 * Modals not listed here do not require entity fetching.
 */
const MODAL_ENTITY_TYPES: Record<string, string> = {
  document_review: 'document',
  ap_posting_approval: 'ap_posting',
  edit_chain: 'approval_chain',
  add_member: 'org_unit',
  reference_selection: 'document',
  item_selection: 'ap_line_item',
};

/**
 * ModalHost reads the active modal from the modal store and renders
 * the corresponding modal component.  Entity data is fetched via
 * useEntityForModal when the modal requires it.
 */
export const ModalHost = () => {
  const { activeModal, entityId, closeModal } = useModalStore();

  const entityType = activeModal ? MODAL_ENTITY_TYPES[activeModal] ?? null : null;
  const { data: entity, isLoading } = useEntityForModal(entityType, entityId);

  if (!activeModal) return null;

  if (isLoading && entityType && entityId) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 text-center">
          <div className="animate-spin h-8 w-8 border-2 border-current border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  switch (activeModal) {
    case 'document_review':
      return entity ? (
        <DocumentReviewModal
          isOpen={true}
          document={entity}
          onClose={closeModal}
        />
      ) : null;

    case 'ap_posting_approval':
      return entity ? (
        <APPostingApprovalModal
          posting={entity}
          isOpen={true}
          onClose={closeModal}
        />
      ) : null;

    case 'edit_chain':
      return (
        <EditChainModal
          chainId={entityId ?? ''}
          onClose={closeModal}
          onSuccess={closeModal}
          simpleMode={false}
        />
      );

    case 'create_hierarchy':
      return (
        <CreateHierarchyModal
          isOpen={true}
          isLoading={false}
          onSubmit={closeModal as any}
          onClose={closeModal}
        />
      );

    case 'add_member':
      return entity ? (
        <AddMemberModal
          isOpen={true}
          isLoading={false}
          unitName={entity.name ?? ''}
          availableUsers={entity.available_users ?? []}
          onSubmit={closeModal as any}
          onClose={closeModal}
        />
      ) : null;

    case 'reference_selection':
      return entity ? (
        <ReferenceSelectionModal
          missingReference={entity}
          onSuccess={closeModal}
          onClose={closeModal}
        />
      ) : null;

    case 'item_selection':
      return entity ? (
        <ItemSelectionModal
          lineItem={entity}
          expenseAccounts={entity.expense_accounts ?? []}
          onSelectItem={closeModal as any}
          onCreateItem={closeModal as any}
          onCancel={closeModal}
        />
      ) : null;

    default:
      console.warn(`[ModalHost] Unknown modal type: "${activeModal}"`);
      return null;
  }
};
